import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router";
import { DASHBOARD_URL, VALIDATION_PATTERNS } from "../../constants/app";
import { sendOtp, verifyOTP } from "../../service/Auth.services";
import OTPModal from "components/Modals/SignInOtpModal";
import ShowToast from "components/ShowToast";
import APP_CONFIG from "../../constants/app";

/* ─────────────────────────────────────────────────────────
   Fintech Light Mode Theme — Clean, Modern & High-Conversion
───────────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  /* ── CSS Reset & Font Scoping ── */
  .mk-login-root *, .mk-login-root *::before, .mk-login-root *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .mk-login-root {
    min-height: 100vh;
    display: flex;
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #f8fafc;
    color: #0f172a;
    position: relative;
    overflow-x: hidden;
  }

  /* ── Background Ambient Glows & Grid ── */
  .mk-bg-decor {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;
  }

  .mk-bg-grid {
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(226, 232, 240, 0.6) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(226, 232, 240, 0.6) 1px, transparent 1px);
    background-size: 40px 40px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 60%, transparent 100%);
    -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 60%, transparent 100%);
  }

  .mk-bg-glow-1 {
    position: absolute;
    width: 650px;
    height: 650px;
    top: -150px;
    left: -100px;
    background: radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, rgba(59, 130, 246, 0.02) 50%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  .mk-bg-glow-2 {
    position: absolute;
    width: 550px;
    height: 550px;
    bottom: -100px;
    right: 25%;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.07) 0%, rgba(5, 150, 105, 0.02) 50%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  .mk-bg-glow-3 {
    position: absolute;
    width: 450px;
    height: 450px;
    top: 30%;
    right: -100px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  /* ── Layout Panels ── */
  .mk-main-container {
    display: flex;
    width: 100%;
    min-height: 100vh;
    position: relative;
    z-index: 1;
  }

  /* ── Left Hero Showcase Panel ── */
  .mk-hero-side {
    flex: 1.15;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 48px 56px;
    position: relative;
    z-index: 2;
  }

  .mk-brand-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 32px;
  }

  .mk-logo-img {
    height: 40px;
    max-width: 180px;
    object-fit: contain;
  }

  .mk-brand-fallback {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mk-brand-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #2563eb, #059669);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-weight: 800;
    font-size: 18px;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
  }

  .mk-brand-name {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #0f172a;
  }

  .mk-hero-content {
    max-width: 580px;
    margin: auto 0;
    padding: 20px 0;
  }

  .mk-tag-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    padding: 6px 14px;
    border-radius: 100px;
    font-size: 12px;
    font-weight: 600;
    color: #2563eb;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  }

  .mk-tag-pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
    animation: mk-pulse-anim 2s infinite;
  }

  @keyframes mk-pulse-anim {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(0.9); }
  }

  .mk-hero-title {
    font-size: clamp(2rem, 3.2vw, 2.75rem);
    font-weight: 800;
    line-height: 1.18;
    letter-spacing: -0.03em;
    color: #0f172a;
    margin-bottom: 16px;
  }

  .mk-hero-title .mk-highlight-blue {
    color: #2563eb;
    background: linear-gradient(135deg, #1d4ed8, #2563eb);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .mk-hero-title .mk-highlight-green {
    color: #059669;
    background: linear-gradient(135deg, #059669, #10b981);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .mk-hero-desc {
    font-size: 15px;
    line-height: 1.65;
    color: #64748b;
    margin-bottom: 32px;
    font-weight: 450;
  }

  /* ── 3 Feature Pillars: Payout, Wallet, Ledger ── */
  .mk-features-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 36px;
  }

  .mk-feature-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 18px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.03);
    backdrop-filter: blur(10px);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }

  .mk-feature-card:hover {
    transform: translateY(-2px);
    border-color: #cbd5e1;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  }

  .mk-feature-icon-box {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  .mk-icon-payout {
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #dbeafe;
  }

  .mk-icon-wallet {
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #d1fae5;
  }

  .mk-icon-ledger {
    background: #faf5ff;
    color: #7c3aed;
    border: 1px solid #f3e8ff;
  }

  .mk-feature-info {
    flex: 1;
    min-width: 0;
  }

  .mk-feature-heading-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  .mk-feature-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .mk-feature-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .mk-badge-blue { background: #dbeafe; color: #1e40af; }
  .mk-badge-green { background: #d1fae5; color: #065f46; }
  .mk-badge-purple { background: #ede9fe; color: #5b21b6; }

  .mk-feature-subtitle {
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
  }

  /* ── Hero Footer Metrics ── */
  .mk-hero-footer {
    display: flex;
    align-items: center;
    gap: 28px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
  }

  .mk-stat-item {
    display: flex;
    flex-direction: column;
  }

  .mk-stat-number {
    font-family: 'JetBrains Mono', monospace;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }

  .mk-stat-label {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  /* ── Right Form Container Panel ── */
  .mk-form-side {
    width: 480px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 36px;
    position: relative;
    z-index: 2;
  }

  .mk-card-wrapper {
    width: 100%;
    max-width: 420px;
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 20px;
    padding: 36px 32px;
    box-shadow: 
      0 20px 40px -15px rgba(15, 23, 42, 0.07),
      0 1px 3px rgba(15, 23, 42, 0.04);
    position: relative;
  }

  .mk-form-header {
    margin-bottom: 28px;
  }

  .mk-card-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #059669;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    padding: 4px 10px;
    border-radius: 6px;
    margin-bottom: 12px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  .mk-form-heading {
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
    margin-bottom: 6px;
  }

  .mk-form-subtext {
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
  }

  /* ── Error Banner ── */
  .mk-alert-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: mk-shake 0.3s ease-in-out;
  }

  @keyframes mk-shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }

  /* ── Form Inputs & Floating States ── */
  .mk-input-group {
    margin-bottom: 18px;
  }

  .mk-input-label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
  }

  .mk-input-label {
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    letter-spacing: 0.02em;
  }

  .mk-input-control {
    position: relative;
    display: flex;
    align-items: center;
  }

  .mk-control-icon {
    position: absolute;
    left: 14px;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    transition: color 0.15s ease;
  }

  .mk-text-input {
    width: 100%;
    height: 48px;
    padding: 0 44px 0 42px;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
    outline: none;
    transition: all 0.2s ease;
  }

  .mk-text-input::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }

  .mk-text-input:focus {
    background: #ffffff;
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
  }

  .mk-text-input:focus ~ .mk-control-icon {
    color: #2563eb;
  }

  .mk-input-has-error {
    border-color: #ef4444 !important;
    background: #fffbfa !important;
  }

  .mk-input-has-error:focus {
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1) !important;
  }

  .mk-input-has-error ~ .mk-control-icon {
    color: #ef4444 !important;
  }

  .mk-toggle-pw-btn {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: color 0.15s ease, background-color 0.15s ease;
  }

  .mk-toggle-pw-btn:hover {
    color: #334155;
    background-color: #f1f5f9;
  }

  .mk-field-error {
    font-size: 11px;
    font-weight: 600;
    color: #ef4444;
    margin-top: 5px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /* ── Remember & Forgot Password ── */
  .mk-action-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  .mk-remember-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  }

  .mk-checkbox {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    accent-color: #2563eb;
    cursor: pointer;
  }

  .mk-remember-label {
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
  }

  .mk-forgot-link {
    font-size: 12px;
    font-weight: 600;
    color: #2563eb;
    text-decoration: none;
    transition: color 0.15s ease;
  }

  .mk-forgot-link:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  /* ── Submit Action Button ── */
  .mk-submit-btn {
    width: 100%;
    height: 48px;
    background: linear-gradient(135deg, #0f172a, #1e293b);
    border: none;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.18);
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
  }

  .mk-submit-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #1e293b, #334155);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.24);
  }

  .mk-submit-btn:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
  }

  .mk-submit-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  /* ── Divider & Register Link ── */
  .mk-card-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 22px 0 18px 0;
  }

  .mk-divider-bar {
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  .mk-divider-label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .mk-register-box {
    text-align: center;
    font-size: 13px;
    color: #64748b;
  }

  .mk-register-link {
    color: #2563eb;
    font-weight: 700;
    text-decoration: none;
    margin-left: 4px;
    transition: color 0.15s ease;
  }

  .mk-register-link:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  /* ── Trust & Security Badges Footer ── */
  .mk-security-badges {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px dashed #e2e8f0;
  }

  .mk-security-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10.5px;
    font-weight: 600;
    color: #64748b;
    letter-spacing: 0.02em;
  }

  .mk-security-icon {
    color: #059669;
    font-size: 12px;
  }

  /* ── Responsive Behavior ── */
  @media (max-width: 1024px) {
    .mk-hero-side {
      display: none;
    }
    .mk-form-side {
      width: 100%;
      max-width: 500px;
      margin: 0 auto;
      padding: 32px 20px;
    }
    .mk-card-wrapper {
      max-width: 100%;
      padding: 32px 24px;
    }
  }

  @media (max-width: 480px) {
    .mk-form-side {
      padding: 20px 14px;
    }
    .mk-card-wrapper {
      padding: 24px 18px;
      border-radius: 16px;
    }
    .mk-form-heading {
      font-size: 21px;
    }
  }
`;

/* ── SVGs for high crispness & no external icon font lag ── */
const PhoneIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const LockIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeOpenIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const PayoutIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const WalletIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
    <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
    <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z" />
  </svg>
);

const LedgerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <line x1="8" y1="7" x2="16" y2="7" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ─────────────────────────────────────────────────────────
   Main SignIn Component
───────────────────────────────────────────────────────── */
const SignIn = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpData, setOtpData] = useState({ phoneNumber: "", userToken: "" });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    localStorage.clear();
    sessionStorage.clear();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Phone number is required";
    } else if (!VALIDATION_PATTERNS.PHONE.test(formData.email)) {
      newErrors.email = "Please enter a valid 10-digit phone number";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      _formData.append("password", formData.password);
      const result = await sendOtp(_formData);
      if (!result.status) {
        const msg = result.message || "Login failed. Please verify your phone number and password.";
        setErrors({ general: msg });
        ShowToast(msg, "error");
      }
      if (result.status) {
        setOtpData({
          phoneNumber: formData.email,
          userToken: result.data?.user_token || "",
        });
        const msg = result.message || "OTP sent successfully to your phone.";
        ShowToast(msg, "success");
        setOtpStep(true);
      }
    } catch (error) {
      console.error("Login error:", error);
      const msg = "An unexpected network error occurred. Please try again.";
      setErrors({ general: msg });
      ShowToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSuccess = (result) => {
    if (result.token) localStorage.setItem("authToken", result.token);
    if (result.data) {
      localStorage.setItem("userid", result.data.id);
      localStorage.setItem("name", result.data.name);
      localStorage.setItem("role", result.data.role);
      localStorage.setItem("islogin", true);
      navigate(DASHBOARD_URL[result.data.role] || "/admin_dashboard", { replace: true });
      return;
    }
    navigate("/", { replace: true });
  };

  const handleOTPError = (error) => {
    console.error("OTP verification error:", error);
  };

  const handleResendOTP = async () => {
    try {
      const _formData = new FormData();
      _formData.append("phone", formData.email);
      _formData.append("password", formData.password);
      const result = await sendOtp(_formData);
      if (result.status) {
        setOtpData((prev) => ({
          ...prev,
          userToken: result.data?.user_token || prev.userToken,
        }));
      } else {
        throw new Error(result.data?.error || "OTP resend failed");
      }
      return result;
    } catch (error) {
      console.error("Resend OTP error:", error);
      throw error;
    }
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setOtpData({ phoneNumber: "", userToken: "" });
    setErrors({});
  };

  if (otpStep) {
    return (
      <OTPModal
        phoneNumber={otpData.phoneNumber}
        password={formData.password}
        userToken={otpData.userToken}
        otpLength={4}
        onSuccess={handleOTPSuccess}
        onError={handleOTPError}
        onResend={handleResendOTP}
        onBack={handleBackToLogin}
        verifyOTPService={verifyOTP}
        resendWaitMinutes={1}
        maxResendAttempts={3}
        autoSubmitOnComplete={false}
        title="Verify OTP"
        subtitle="Enter the 4-digit security code sent to your mobile phone"
      />
    );
  }

  const brandName = APP_CONFIG.ORGANISATION_BRAND || "Mokshiq";
  const brandLogo = APP_CONFIG.ORGANISATION_LOGO;

  return (
    <>
      <style>{styles}</style>

      <div className="mk-login-root">
        {/* Background glow and decorative elements */}
        <div className="mk-bg-decor" aria-hidden="true">
          <div className="mk-bg-grid" />
          <div className="mk-bg-glow-1" />
          <div className="mk-bg-glow-2" />
          <div className="mk-bg-glow-3" />
        </div>

        <div className="mk-main-container">
          {/* ── Left Hero Showcase Panel ── */}
          <div className="mk-hero-side">
            <div className="mk-brand-header">
              {brandLogo ? (
                <img src={brandLogo} alt={brandName} className="mk-logo-img" />
              ) : (
                <div className="mk-brand-fallback">
                  <div className="mk-brand-icon">M</div>
                  <span className="mk-brand-name">{brandName}</span>
                </div>
              )}
            </div>

            <div className="mk-hero-content">
              <div className="mk-tag-pill">
                <span className="mk-tag-pulse" />
                <span>One Secure Dashboard • Next-Gen Fintech</span>
              </div>

              <h1 className="mk-hero-title">
                <span className="mk-highlight-blue">Payout</span>,{" "}
                <span className="mk-highlight-green">Wallet</span> &{" "}
                Ledger in One Secure Dashboard.
              </h1>

              <p className="mk-hero-desc">
                Streamline merchant banking, instant bank settlements, and multi-tier
                wallet management with real-time passbooks and bank-grade encryption.
              </p>

              {/* 3 Core Value Proposition Feature Cards */}
              <div className="mk-features-grid">
                {/* 1. Instant Payout Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-payout">
                    <PayoutIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Instant Payouts</span>
                      <span className="mk-feature-badge mk-badge-blue">24/7 IMPS/NEFT</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Sub-second automated bank transfers & bulk merchant payouts with instant UTR tracking.
                    </p>
                  </div>
                </div>

                {/* 2. Multi-Wallet Control Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-wallet">
                    <WalletIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Multi-Wallet Control</span>
                      <span className="mk-feature-badge mk-badge-green">Main & BC Wallet</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Intra-wallet balance transfers, QR top-ups, and automated low-balance safeguards.
                    </p>
                  </div>
                </div>

                {/* 3. Audit-Ready Ledger Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-ledger">
                    <LedgerIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Audit-Ready Ledger</span>
                      <span className="mk-feature-badge mk-badge-purple">100% Reconciled</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Immutable double-entry transaction log with automated GST & CA reporting exports.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Footer Stats */}
            <div className="mk-hero-footer">
              <div className="mk-stat-item">
                <span className="mk-stat-number">99.98%</span>
                <span className="mk-stat-label">Platform Uptime</span>
              </div>
              <div className="mk-stat-item">
                <span className="mk-stat-number">₹450M+</span>
                <span className="mk-stat-label">Monthly Volume</span>
              </div>
              <div className="mk-stat-item">
                <span className="mk-stat-number">256-Bit</span>
                <span className="mk-stat-label">Bank Encryption</span>
              </div>
            </div>
          </div>

          {/* ── Right Form Container Panel ── */}
          <div className="mk-form-side">
            <div className="mk-card-wrapper">
              <div className="mk-form-header">
                <div className="mk-card-pill">
                  <ShieldCheckIcon />
                  <span>Verified Merchant Portal</span>
                </div>
                <h2 className="mk-form-heading">Welcome Back</h2>
                <p className="mk-form-subtext">
                  Sign in with your phone number to access your secure wallet & ledger dashboard.
                </p>
              </div>

              {/* General Error Alert */}
              {errors.general && (
                <div className="mk-alert-error" role="alert">
                  <span>⚠️</span>
                  <span>{errors.general}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Phone Input */}
                <div className="mk-input-group">
                  <div className="mk-input-label-row">
                    <label className="mk-input-label" htmlFor="phone-input">
                      Mobile Number
                    </label>
                  </div>
                  <div className="mk-input-control">
                    <span className="mk-control-icon">
                      <PhoneIcon />
                    </span>
                    <input
                      id="phone-input"
                      name="email"
                      type="tel"
                      className={`mk-text-input${errors.email ? " mk-input-has-error" : ""}`}
                      placeholder="Enter 10-digit mobile number"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                      autoComplete="tel"
                      maxLength={10}
                    />
                  </div>
                  {errors.email && (
                    <div className="mk-field-error">
                      <span>⚠</span>
                      <span>{errors.email}</span>
                    </div>
                  )}
                </div>

                {/* Password Input */}
                <div className="mk-input-group">
                  <div className="mk-input-label-row">
                    <label className="mk-input-label" htmlFor="password-input">
                      Password
                    </label>
                  </div>
                  <div className="mk-input-control">
                    <span className="mk-control-icon">
                      <LockIcon />
                    </span>
                    <input
                      id="password-input"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      className={`mk-text-input${errors.password ? " mk-input-has-error" : ""}`}
                      placeholder="Enter your account password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="mk-toggle-pw-btn"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                    </button>
                  </div>
                  {errors.password && (
                    <div className="mk-field-error">
                      <span>⚠</span>
                      <span>{errors.password}</span>
                    </div>
                  )}
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="mk-action-row">
                  <label className="mk-remember-wrap">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      className="mk-checkbox"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <span className="mk-remember-label">Remember me</span>
                  </label>
                  <Link to="/forgot_password" className="mk-forgot-link">
                    Forgot Password?
                  </Link>
                </div>

                {/* Submit Sign In Button */}
                <button
                  type="submit"
                  className="mk-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                        style={{ width: "1.1rem", height: "1.1rem", borderWidth: "2px" }}
                      />
                      <span>Signing in securely…</span>
                    </>
                  ) : (
                    <>
                      <span>Sign In to Dashboard</span>
                      <ArrowRightIcon />
                    </>
                  )}
                </button>
              </form>

              {/* Security Trust Badges */}
              <div className="mk-security-badges">
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>256-Bit SSL</span>
                </div>
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>OTP Protected</span>
                </div>
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>PCI DSS Ready</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;